/**
 * Card video previews on collection pages.
 * ----------------------------------------------------------------------------
 * Each <video data-pi-video data-start data-end> plays a slice of its clip while the card
 * is on screen, then loops back to the start second. The rest of the file is never needed,
 * so playback seeks straight to `start` and never runs past `end`.
 *
 * WHY THE LIMITS BELOW EXIST
 * A collection page can hold thirty cards. Thirty simultaneous videos would saturate a
 * phone's decoder and its data plan for a result nobody can watch, so:
 *   - nothing is fetched until a card is near the viewport (preload="none" in the markup),
 *   - at most MAX_PLAYING clips run at once, nearest to the middle of the screen first,
 *   - a clip that scrolls away is paused and released.
 *
 * It also declines entirely on Save-Data and on prefers-reduced-motion, where an
 * autoplaying loop is either expensive or unwelcome.
 */
(function () {
  var MAX_PLAYING = 3;
  var VIS_RATIO = 0.5;      // half the card on screen before it earns a play
  var SEL = '[data-pi-video]';

  function optedOut() {
    try {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return true;
      var c = navigator.connection;
      if (c && c.saveData) return true;
    } catch (e) {}
    return false;
  }
  if (optedOut()) {
    // The markup may have painted a poster over the product photo. Nothing is going to
    // play, so hand the card back to its photograph.
    document.documentElement.setAttribute('data-pi-off', '');
    return;
  }

  var visible = new Set();

  function centreDistance(el) {
    var r = el.getBoundingClientRect();
    return Math.abs((r.top + r.bottom) / 2 - (window.innerHeight || 0) / 2);
  }

  function stop(v) {
    if (v.paused && !v.classList.contains('is-playing')) return;
    v.classList.remove('is-playing');
    try { v.pause(); } catch (e) {}
    // Dropping the current time keeps a re-entry starting from the intended second rather
    // than wherever it happened to stop.
    try { v.currentTime = start(v); } catch (e) {}
  }

  function start(v) { return parseFloat(v.getAttribute('data-start')) || 0; }
  function end(v) {
    var e = parseFloat(v.getAttribute('data-end'));
    return (isFinite(e) && e > start(v)) ? e : Infinity;
  }

  /**
   * The clip covers the whole image area, so while it plays the card's photo swiper is
   * running underneath where nobody can see it — dots advancing, slides moving, all
   * invisible. The moment the customer actually engages with the photos, the video gets out
   * of the way and stays out: they have chosen to browse stills, and having the clip fade
   * back over them a second later would be worse than never showing it.
   *
   * Bound to the track's own scroll and to the arrows rather than to pointerdown, because a
   * plain tap is how you open the product — that should not count as "show me the photos".
   */
  function dismiss(v) {
    if (v.dataset.piDismissed === '1') return;
    v.dataset.piDismissed = '1';
    visible.delete(v);
    stop(v);
    v.classList.add('pi-video--dismissed');
  }

  function bindSwipeHandover(v) {
    if (v.dataset.piSwipeBound === '1') return;
    v.dataset.piSwipeBound = '1';
    var card = v.closest('.product-item__aspect-ratio') || v.parentElement;
    if (!card) return;
    var track = card.querySelector('[data-pi-track]');
    if (track) track.addEventListener('scroll', function () { dismiss(v); }, { passive: true, once: true });
    card.querySelectorAll('[data-pi-prev],[data-pi-next]').forEach(function (b) {
      b.addEventListener('click', function () { dismiss(v); });
    });
  }

  function play(v) {
    if (v.dataset.piDismissed === '1') return;
    if (!v.paused) return;
    warm(v);
    if (v.dataset.piBound !== '1') {
      v.dataset.piBound = '1';
      // Seeking on loadedmetadata rather than before it: currentTime is ignored while the
      // duration is still unknown, which is why the clip would otherwise start at 0.
      v.addEventListener('loadedmetadata', function () {
        try { v.currentTime = start(v); } catch (e) {}
      });
      // The clamp is what makes a segment loop. `loop` on the element is only the safety
      // net for a clip played end to end: it returns to 0, and the next tick pulls it back
      // to `start` if the segment does not begin there.
      v.addEventListener('timeupdate', function () {
        if (v.currentTime >= end(v) || v.currentTime < start(v) - 0.25) {
          try { v.currentTime = start(v); } catch (e) {}
        }
      });
      v.addEventListener('playing', function () { v.classList.add('is-playing'); });
    }
    if (v.readyState >= 1 && Math.abs(v.currentTime - start(v)) > end(v) - start(v)) {
      try { v.currentTime = start(v); } catch (e) {}
    }
    var p = v.play();
    // Autoplay can still be refused (a muted attribute lost to a DOM copy, a strict
    // policy). Failing quietly leaves the image showing, which is a fine outcome.
    if (p && p.catch) p.catch(function () { v.classList.remove('is-playing'); });
  }

  function reconcile() {
    var list = Array.from(visible).filter(function (v) { return document.contains(v); });
    list.sort(function (a, b) { return centreDistance(a) - centreDistance(b); });
    list.forEach(function (v, i) { if (i < MAX_PLAYING) play(v); else stop(v); });
    document.querySelectorAll(SEL).forEach(function (v) { if (!visible.has(v)) stop(v); });
  }

  /**
   * Buffering starts long before the card is on screen. This is the whole reason a clip
   * used to appear four or five seconds late: with preload="none" and a play trigger at
   * 50% visibility, the fetch, the seek and the decode all happened while the customer was
   * already looking at the card. Warming a screen and a half early means the first frame
   * is usually ready by the time the card arrives.
   *
   * It is still not preload on page load: a card that never gets scrolled to costs nothing.
   */
  function warm(v) {
    if (v.dataset.piWarm === '1') return;
    v.dataset.piWarm = '1';
    try { v.preload = 'auto'; v.load(); } catch (e) {}
  }

  var ioWarm = new IntersectionObserver(function (entries) {
    entries.forEach(function (en) { if (en.isIntersecting) warm(en.target); });
  }, { rootMargin: '1500px 0px' });

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      if (en.target.dataset.piDismissed === '1') visible.delete(en.target);
      else if (en.isIntersecting && en.intersectionRatio >= VIS_RATIO) visible.add(en.target);
      else visible.delete(en.target);
    });
    reconcile();
  }, { threshold: [0, VIS_RATIO, 1], rootMargin: '100px 0px' });

  function observeAll() {
    document.querySelectorAll(SEL).forEach(function (v) {
      if (v.dataset.piObserved === '1') return;
      v.dataset.piObserved = '1';
      bindSwipeHandover(v);
      ioWarm.observe(v);
      io.observe(v);
    });
  }

  /**
   * Geometry fallback. IntersectionObserver does not fire while the tab is hidden and can
   * be missed after a programmatic scroll, which would leave a card sitting on a still
   * image with no way to recover. This recomputes the visible set directly.
   *
   * Throttled on a timestamp rather than requestAnimationFrame: rAF is paused in a hidden
   * tab too, so an rAF guard would take the fallback down with the thing it is backing up.
   */
  var lastCheck = 0;
  function checkAll(force) {
    var now = Date.now();
    if (!force && now - lastCheck < 150) return;
    lastCheck = now;
    var vh = window.innerHeight || 0;
    document.querySelectorAll(SEL).forEach(function (v) {
      var r = v.getBoundingClientRect();
      if (!r.height) return;
      if (r.top < vh + 1500 && r.bottom > -1500) warm(v);
      var shown = Math.min(r.bottom, vh) - Math.max(r.top, 0);
      if (v.dataset.piDismissed === '1') { visible.delete(v); return; }
      if (shown / r.height >= VIS_RATIO) visible.add(v); else visible.delete(v);
    });
    reconcile();
  }

  // Cards arrive later on infinite-scroll collections and after instant navigation, so the
  // set of videos is not fixed at load.
  function watch() {
    // Mark anything the markup set to autoplay as already warm, so warm() does not call
    // load() on a video the browser is in the middle of streaming and reset its progress.
    document.querySelectorAll(SEL).forEach(function (v) {
      if (v.hasAttribute('autoplay')) v.dataset.piWarm = '1';
    });
    observeAll();
    checkAll(true);
    new MutationObserver(function () { observeAll(); checkAll(true); })
      .observe(document.body, { childList: true, subtree: true });
    window.addEventListener('scroll', function () { checkAll(false); }, { passive: true });
    window.addEventListener('resize', function () { checkAll(false); }, { passive: true });
    // A hidden tab suppresses IntersectionObserver; re-check when it comes back.
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') checkAll(true);
      else document.querySelectorAll(SEL).forEach(stop);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
  else watch();
})();
